import React, { useState } from 'react';
import Button from '../../../../../../core/button/Button';
import LabelledSwitch from '../../../../../../core/switch/LabelledSwitch';
import ColorPicker from '../../../../../../core/inputs/ColorPicker';
import Seperator from '../../../../../../core/seperator/Seperator';

interface StyleCondition {
  property: string;
  value: string;
  color: string;
  opacity: number;
}

interface SettingsPanelProps {
  onDepthTestChange: (enabled: boolean) => void;
  onShadowsChange: (enabled: boolean) => void;
  conditions: StyleCondition[];
  onStyleConditionsChange: (conditions: StyleCondition[]) => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ 
  onDepthTestChange, 
  onShadowsChange,
  conditions,
  onStyleConditionsChange 
}) => {
  const [depthTestEnabled, setDepthTestEnabled] = useState(false);
  const [shadowsEnabled, setShadowsEnabled] = useState(true);

  const handleDepthTestChange = (enabled: boolean) => {
    setDepthTestEnabled(enabled);
    onDepthTestChange(enabled);
  };

  const handleShadowsChange = (enabled: boolean) => {
    setShadowsEnabled(enabled);
    onShadowsChange(enabled);
  };

  const handleRemoveCondition = (index: number) => {
    const updatedConditions = conditions.filter((_, i) => i !== index);
    onStyleConditionsChange(updatedConditions);
  };

  const handleColorChange = async (index: number, color: string, opacity: number) => {
    const updatedConditions = conditions.map((condition, i) => {
      if (i === index) {
        return { ...condition, color, opacity };
      }
      return condition;
    });
    onStyleConditionsChange(updatedConditions);
  };

  const handleToggleVisibility = (index: number) => {
    const condition = conditions[index];
    const newOpacity = condition.opacity === 0 ? 1.0 : 0;
    handleColorChange(index, condition.color, newOpacity);
  };

  return (
    <div className="fixed top-20 left-4 z-50 w-80 bg-surface-default border border-border-default rounded-2xl shadow-lg p-6 max-h-[80vh] overflow-y-auto">
      <h2 className="text-xl font-semibold text-text-default mb-6">Settings</h2>
      
      <div className="space-y-4">
        <LabelledSwitch
          name="depthTest"
          label="Depth Test Against Terrain"
          toggled={depthTestEnabled}
          handleSetToggle={handleDepthTestChange}
        />
        
        <LabelledSwitch
          name="shadows"
          label="Enable Shadows"
          toggled={shadowsEnabled}
          handleSetToggle={handleShadowsChange}
        />
      </div>

      {conditions.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-medium text-text-default mb-4">Style Conditions</h3>
          
          <div className="space-y-0">
            {conditions.map((condition, index) => (
              <div key={index}>
                <div className="relative py-4">
                  <div className="absolute top-2 right-0 flex gap-1">
                  <ColorPicker
                        initialColor={condition.color}
                        initialOpacity={condition.opacity}
                        handleSave={async (color, opacity) => handleColorChange(index, color, opacity)}
                      />

                    <Button
                      variant="shapeless"
                      size="sm"
                      square
                      leadingIcon={condition.opacity === 0 ? "eye-strikethrough" : "eye"}
                      onClick={() => handleToggleVisibility(index)}
                      className=""
                    />
                    <Button
                      variant="shapeless"
                      size="sm"
                      square
                      leadingIcon="trash"
                      critical
                      onClick={() => handleRemoveCondition(index)}
                      className=""
                    />
                  </div>
                  
                  <div className="space-y-3 pr-20">
                    <div className="">
                      <label className="body-sm font-medium text-text-subdued">Property & Value</label>
                      <p className="body-sm text-text-default mt-2">
                        {condition.property || 'No property'}
                      </p>
                      <p className=" body-sm-bold text-text-default">
                        {condition.value || 'No value'}
                      </p>
                    </div> 
                  </div>
                </div>
                
                {index < conditions.length - 1 && <Seperator />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel; 